import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Box,
	Container,
	GlobalStyles,
	IconButton,
	IconButtonProps,
	Link,
	Stack,
	Typography,
	useColorScheme,
} from '@mui/joy';
import React from 'react';
import { Outlet } from 'react-router-dom';

/**
 * Layout for all conversion pages (login, register, forgot password, etc.)
 */
const ConversionLayout: React.FC = (props) => {
	return (
		<Styles className="landing-layout">
			<Box position="relative" className="main" bgcolor="common.black">
				<Stack width="100%" bgcolor="common.black" p={2}>
					<Container maxWidth="md">
						<Box>
							<img
								src="https://mod-acorn.s3.eu-west-1.amazonaws.com/gok-uk-logo.svg"
								alt="GOK UK"
							/>
						</Box>
						<Typography
							textAlign="center"
							mt={-4}
							level="title-md"
							textColor="common.white"
						>
							MCTC Learner Platform
						</Typography>
					</Container>
				</Stack>
				<Container maxWidth="md">
					<Box
						width="100%"
						height={10}
						sx={(theme) => ({ backgroundColor: theme.vars.palette.primary[500] })}
					></Box>
				</Container>
				<Outlet />
			</Box>

			<Box
				mt={8}
				pb={{ xs: 14, md: 8 }}
				border="2px solid"
				position="relative"
				sx={(theme) => ({
					backgroundColor: theme.vars.palette.background.backdrop,
					borderColor: theme.vars.palette.divider,
				})}
			>
				<Container maxWidth="md" sx={{ height: '200px', py: 8 }}>
					<Stack
						direction={{ xs: 'column', md: 'row' }}
						spacing={{ xs: 2, md: 10 }}
						alignItems={{ xs: 'start', md: 'row' }}
					>
						<Stack flex={1} spacing={2} height="100%" justifyContent="end">
							<Stack direction="row" alignItems="center" spacing={4}>
								<Link textColor="text.primary" href="/help">
									Help
								</Link>
								<Link textColor="text.primary" href="/terms-and-conditions">
									Terms and conditions
								</Link>
								<Link textColor="text.primary" href="/privacy-policy">
									Privacy Policy
								</Link>
							</Stack>
							<Stack direction="row" alignItems="center" spacing={2}>
								<img
									src="https://mod-acorn.s3.eu-west-1.amazonaws.com/ogl-logo.svg"
									alt="OGL Logo"
								/>
								<Typography level="body-sm" textColor="text.primary">
									All content is available under the{' '}
									<Link textColor="text.primary"> Open Government Licence</Link>{' '}
									v3.0, except where otherwise stated
								</Typography>
							</Stack>
						</Stack>
						<Stack height="100%">
							<img
								src="https://mod-acorn.s3.eu-west-1.amazonaws.com/crown-logo.svg"
								alt="Crown Logo"
								style={{ maxHeight: '100%' }}
							/>
							<Link textColor="text.primary">© Crown Copyright</Link>
						</Stack>
					</Stack>
				</Container>
			</Box>
		</Styles>
	);
};

export default ConversionLayout;

const Styles = styled.div`
	background-repeat: no-repeat;
	background-size: cover;
	background-position: center;

	${({ theme }) => theme.breakpoints.up('md')} {
		background-position: 250px;
	}
	&.landing-layout {
		height: 100%;
		display: flex;
		flex-direction: column;

		${({ theme }) => theme.breakpoints.down('md')} {
			height: fit-content;
		}

		.sidebar {
			flex: 0 0 30%;
			display: flex;
			align-items: center;
			padding: ${({ theme }) => theme.spacing(2)};
			color: ${({ theme }) => theme.palette.common.white};

			.logo {
				max-width: 150px;
			}
		}

		.main {
			flex: 1 0 auto;
			display: flex;
			flex-direction: column;
			background-color: ${({ theme }) => theme.palette.background.body};
		}
	}
`;

export const ColorSchemeToggle = ({ onClick, ...props }: IconButtonProps) => {
	const { mode, setMode } = useColorScheme();
	const [mounted, setMounted] = React.useState(false);
	React.useEffect(() => {
		setMounted(true);
	}, []);
	if (!mounted) {
		return <IconButton size="sm" variant="plain" color="neutral" disabled />;
	}
	return (
		<IconButton
			id="toggle-mode"
			size="sm"
			variant="plain"
			sx={(theme) => ({ color: theme.vars.palette.text.tertiary })}
			aria-label="toggle light/dark mode"
			{...props}
			onClick={(event) => {
				if (mode === 'light') {
					setMode('dark');
				} else {
					setMode('light');
				}
				onClick?.(event);
			}}
		>
			{mode === 'light' ? (
				<FontAwesomeIcon icon={['fas', 'moon']} />
			) : (
				<FontAwesomeIcon icon={['fas', 'sun-bright']} />
			)}
		</IconButton>
	);
};
